import type { ReactNode } from 'react'

import { usePageType } from '@/src/general/hooks/usePageType'
import { usePageTypeScreenLayout } from '@/src/general/hooks/usePageTypeScreenLayout'
import { PAGE_BACKGROUND_THEME_COLOR } from '@/src/general/ui/layouts/AppLayout/PageTypeBackground'

type TScreenLayoutProps = { children: ReactNode }

/**
 * Shared page-type defaults for Stack / Drawer / Tabs wrappers.
 */
export const usePageTypeNavigator = () => {
  const { background } = usePageType()
  const pageTypeScreenLayout = usePageTypeScreenLayout()
  const pageBackgroundColor = PAGE_BACKGROUND_THEME_COLOR[background]

  const composeScreenLayout =
    <T extends TScreenLayoutProps>(screenLayout?: (props: T) => ReactNode) =>
    (layoutProps: T) =>
      pageTypeScreenLayout({
        children: screenLayout ? screenLayout(layoutProps) : layoutProps.children,
      })

  return { background, pageBackgroundColor, composeScreenLayout }
}

type TOptionsWithStyle = {
  contentStyle?: unknown
  sceneStyle?: unknown
}

/**
 * Merges an opaque page-type background into navigator screenOptions.
 * Caller style wins when provided last in the StyleProp array.
 */
export const mergePageTypeScreenOptions = <TOptions extends TOptionsWithStyle, TProps>(
  styleKey: 'contentStyle' | 'sceneStyle',
  pageBackgroundColor: string,
  screenOptions?: TOptions | ((props: TProps) => TOptions),
): TOptions | ((props: TProps) => TOptions) => {
  const opaqueStyle = { backgroundColor: pageBackgroundColor }

  if (typeof screenOptions === 'function') {
    return (props: TProps) => {
      const resolved = screenOptions(props)
      return {
        ...resolved,
        [styleKey]: [opaqueStyle, resolved?.[styleKey]],
      }
    }
  }

  return {
    ...screenOptions,
    [styleKey]: [opaqueStyle, screenOptions?.[styleKey]],
  } as TOptions
}
